/**
 * The conversion-attribution contexts: what the user clicked, in which search.
 *
 * Entries are written when a search result is clicked and read, possibly days
 * later and on a different app, when the user completes an action of value.
 * Because `apps/web` and `apps/checkout` live on different subdomains, they
 * travel in a cookie scoped to the parent domain rather than in
 * `sessionStorage` — the same mechanism `eec_checkout_data` already uses in
 * `@24orebs/tracking-lib`.
 *
 * **A short list, not a single slot.** Comparing three masters before choosing
 * one is the normal behaviour of this audience, not an edge case: with one slot
 * the third click destroyed the attribution of the first two, and the landing
 * page had to *delete* the context whenever it did not match. Keyed entries
 * make the conversion look up the course it is actually converting, which is
 * what lets the window be long without ever crediting the wrong record.
 *
 * The key is `courseCode`, not the slug: it is the only identifier present at
 * every conversion point, the checkout included (`eec_checkout_data` carries it
 * as `items[].item_id`), and every course URL spells it out as
 * `/c/<slug>_<courseCode>`.
 *
 * This module is pure serialisation: it never talks to Algolia.
 */
/**
 * Cookie holding the attribution contexts.
 *
 * The `_v2` suffix is what makes the migration from the single-slot format
 * safe: old code can never parse a new-format payload, and vice versa, because
 * neither reads the other's cookie. The legacy `algolia_insights_ctx` expires
 * on its own within an hour of the deploy — its own `max-age` — costing at most
 * one hour of in-flight attributions.
 */
const COOKIE_NAME = 'algolia_insights_ctx_v2';
/** Payload format version, so the next migration has a discriminator to read. */
const FORMAT_VERSION = 2;
/**
 * How long a click stays attributable, in seconds (7 days).
 *
 * Tuned to the decision cycle of an executive master — read, download the
 * brochure, discuss it internally, come back days later and buy — not to a
 * single browsing session. The per-course lookup is what makes a window this
 * long safe.
 */
const MAX_AGE_SECONDS = 604800;
/** The attribution window in milliseconds, for anything with the same lifetime. */
export const INSIGHT_CONTEXT_TTL_MS = MAX_AGE_SECONDS * 1000;
/**
 * How many courses stay attributable at once. Five covers comparing a handful
 * of masters while keeping the cookie — which rides on every request to every
 * subdomain — under a kilobyte.
 */
const MAX_ENTRIES = 5;
/**
 * Upper-cases a course code for comparison.
 *
 * Case discipline matters here: `parseCourseParam` upper-cases, the preview URL
 * builder lower-cases the whole path, and the DatoCMS records are upper-case.
 * The key is normalised on both write and lookup; `objectID` never is, because
 * Algolia needs it exactly as the index spells it. Exported because the
 * conversion guard keys on the same normalised code, and a guard that spelled a
 * course differently from the lookup would not recognise its own duplicates.
 *
 * @param courseCode - Raw course code.
 * @returns The trimmed, upper-cased code, or an empty string.
 */
export function normalizeCourseCode(courseCode) {
    return typeof courseCode === 'string' ? courseCode.trim().toUpperCase() : '';
}
/**
 * Whether an entry is still within the attribution window.
 *
 * Checked per entry rather than relying on the cookie's `max-age`, which every
 * write refreshes — so the cookie routinely outlives its oldest entries.
 *
 * @param entry - The stored entry.
 * @returns `true` when the entry may still be used.
 */
function isFresh(entry) {
    return Date.now() - entry.t <= MAX_AGE_SECONDS * 1000;
}
/**
 * Whether a parsed value has the shape of a usable entry.
 *
 * @param value - Candidate read back from the cookie.
 * @returns `true` when every field needed to send an event is present.
 */
function isStoredEntry(value) {
    const entry = value;
    return (!!entry &&
        typeof entry.q === 'string' &&
        !!entry.q &&
        typeof entry.o === 'string' &&
        !!entry.o &&
        typeof entry.c === 'string' &&
        !!entry.c &&
        typeof entry.t === 'number');
}
/**
 * Reads and validates the cookie payload, dropping expired entries.
 *
 * @returns The payload with only fresh, well-formed entries, or `null`.
 */
function readPayload() {
    if (typeof document === 'undefined')
        return null;
    try {
        const raw = readCookie();
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed.v !== FORMAT_VERSION)
            return null;
        if (typeof parsed.i !== 'string' || !parsed.i)
            return null;
        if (!Array.isArray(parsed.e))
            return null;
        const entries = parsed.e.filter(isStoredEntry).filter(isFresh);
        if (entries.length === 0)
            return null;
        return { v: FORMAT_VERSION, i: parsed.i, e: entries };
    }
    catch {
        return null;
    }
}
/**
 * Serialises the payload into the cookie, or removes the cookie when there is
 * nothing left to store — no point shipping an empty list on every request.
 *
 * @param payload - Entries to persist.
 * @param options - Cookie domain.
 */
function writePayload(payload, options) {
    if (typeof document === 'undefined')
        return;
    try {
        if (payload.e.length === 0) {
            clearInsightContext(options);
            return;
        }
        const value = encodeURIComponent(JSON.stringify(payload));
        const domainPart = options?.domain ? `; domain=${options.domain}` : '';
        document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${domainPart}`;
    }
    catch {
        // Cookies can be unavailable (blocked, storage partitioned). Attribution
        // is lost, nothing else is.
    }
}
/**
 * Converts a stored entry into the shape the rest of the package uses.
 *
 * @param entry - The stored entry.
 * @param index - The payload's hoisted index name.
 * @returns The attribution context.
 */
function toContext(entry, index) {
    return { queryID: entry.q, objectID: entry.o, courseCode: entry.c, index, ts: entry.t };
}
/**
 * Stores an attribution context, most-recent-first.
 *
 * An entry for the same course is replaced rather than duplicated, so the list
 * holds five distinct courses. A write for a different index resets the list:
 * the index name is stored once for the whole payload, so mixing two of them
 * would send conversions against the wrong index after an environment
 * promotion or a reindex under a new name.
 *
 * Does nothing without a `courseCode` — an entry that can never be looked up
 * would only push a usable one off the end of the list.
 *
 * @param input - The clicked record and the search behind it.
 * @param options - Cookie domain.
 */
export function saveInsightContext(input, options) {
    if (typeof document === 'undefined')
        return;
    const courseCode = normalizeCourseCode(input.courseCode);
    if (!courseCode || !input.queryID || !input.objectID || !input.index)
        return;
    try {
        const payload = readPayload();
        const previous = payload && payload.i === input.index ? payload.e : [];
        const entries = [
            { q: input.queryID, o: input.objectID, c: courseCode, t: Date.now() },
            ...previous.filter((entry) => entry.c !== courseCode)
        ].slice(0, MAX_ENTRIES);
        writePayload({ v: FORMAT_VERSION, i: input.index, e: entries }, options);
    }
    catch {
        // ignore — attribution is best-effort
    }
}
/**
 * Reads the attribution context of one course.
 *
 * @param lookup - The course being converted.
 * @returns Its context, or `null` when that course was never clicked from a
 *          search, or the click has expired. A miss is meaningful: it means the
 *          conversion is not attributable to a search, not that another
 *          course's context should be used instead.
 */
export function loadInsightContext(lookup) {
    const courseCode = normalizeCourseCode(lookup.courseCode);
    if (!courseCode)
        return null;
    const payload = readPayload();
    if (!payload)
        return null;
    const entry = payload.e.find((candidate) => candidate.c === courseCode);
    return entry ? toContext(entry, payload.i) : null;
}
/**
 * Reads the most recently stored context, whatever course it belongs to.
 *
 * The escape hatch for a conversion that cannot name its own course — an order
 * whose `eec_checkout_data` expired before this cookie did, for instance. Never
 * call it as a fallback for a lookup that *had* a key and missed: that is
 * exactly the mis-attribution the keyed lookup exists to prevent.
 *
 * @returns The newest context, or `null`.
 */
export function loadMostRecentInsightContext() {
    const payload = readPayload();
    return payload ? toContext(payload.e[0], payload.i) : null;
}
/**
 * Removes one course's context, leaving the others alone.
 *
 * Used when a purchase closes that course's funnel. The visitor may still be
 * considering the other courses in the list.
 *
 * @param courseCode - The course whose context is spent.
 * @param options - Cookie domain. Must match the one used to write it.
 */
export function removeInsightContext(courseCode, options) {
    const normalized = normalizeCourseCode(courseCode);
    if (!normalized)
        return;
    try {
        const payload = readPayload();
        if (!payload)
            return;
        const entries = payload.e.filter((entry) => entry.c !== normalized);
        if (entries.length === payload.e.length)
            return;
        writePayload({ ...payload, e: entries }, options);
    }
    catch {
        // ignore
    }
}
/**
 * Removes every attribution context. Consent withdrawal, not conversion.
 *
 * @param options - Cookie domain. Must match the one used to write it.
 */
export function clearInsightContext(options) {
    if (typeof document === 'undefined')
        return;
    try {
        const domainPart = options?.domain ? `; domain=${options.domain}` : '';
        document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${domainPart}`;
    }
    catch {
        // ignore
    }
}
/**
 * Reads the raw cookie value.
 *
 * @returns The decoded value, or `null` when the cookie is not set.
 */
function readCookie() {
    const prefix = `${COOKIE_NAME}=`;
    for (const entry of document.cookie.split(';')) {
        const trimmed = entry.trim();
        if (trimmed.startsWith(prefix)) {
            return decodeURIComponent(trimmed.slice(prefix.length));
        }
    }
    return null;
}
